import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Input } from 'antd';

import './MobileVirtualKeyboard.css';

const LETTER_ROWS = [
   ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
   ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
   ['shift', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace'],
   ['symbols', ',', 'space', '.', 'left', 'right', 'enter']
];

const SYMBOL_ROWS = [
   ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
   ['@', '#', '$', '%', '&', '-', '+', '(', ')'],
   ['symbols2', '*', '"', "'", ':', ';', '!', '?', 'backspace'],
   ['letters', '/', 'space', '.', ',', 'left', 'right', 'enter']
];

const MORE_SYMBOL_ROWS = [
   ['[', ']', '{', '}', '^', '_', '=', '|', '~', '`'],
   ['\\', '<', '>', '€', '£', '¥', '•', '…', '÷'],
   ['symbols', '©', '®', '™', '✓', '[', ']', '!', '?', 'backspace'],
   ['letters', '/', 'space', '.', ',', 'left', 'right', 'enter']
];

const KEY_LABELS = {
   symbols: '?123',
   symbols2: '=\\<',
   letters: 'ABC',
   space: 'Space',
   left: '←',
   right: '→'
};

const KeyboardKeyIcon = ({ type, locked = false }) => (
   <svg
      className="mobile-virtual-keyboard-key-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
   >
      {type === 'shift' && (
         <>
            <path d="M12 3 4.5 10.5h4V18h7v-7.5h4L12 3Z" />
            {locked && <path d="M8.5 21h7" />}
         </>
      )}
      {type === 'backspace' && (
         <>
            <path d="M20 5.5H9L3 12l6 6.5h11a1 1 0 0 0 1-1v-11a1 1 0 0 0-1-1Z" />
            <path d="m12 9 6 6m0-6-6 6" />
         </>
      )}
      {type === 'enter' && (
         <path d="M20 4v6a4 4 0 0 1-4 4H5m4-4-4 4 4 4" />
      )}
   </svg>
);

const SHIFT_LOCK_DELAY_MS = 450;
const KEY_REPEAT_DELAY_MS = 650;
const KEY_REPEAT_INTERVAL_MS = 110;
const TEXT_SELECTION_HOLD_MS = 550;
const POINTER_DRAG_THRESHOLD_PX = 7;
const SPACE_CURSOR_DRAG_THRESHOLD_PX = 8;
const SPACE_CURSOR_STEP_PX = 12;
const DOUBLE_TAP_DELAY_MS = 350;
const DOUBLE_TAP_DISTANCE_PX = 28;
const KEYBOARD_PREFERENCE_KEY = 'assessment-use-virtual-keyboard';
const KEYBOARD_PREFERENCE_EVENT = 'assessment-keyboard-preference-change';

export const isIPadOSDevice = () => {
   if (typeof navigator === 'undefined') return false;

   const userAgent = navigator.userAgent ?? '';
   const platform = navigator.platform ?? '';
   const hasMultipleTouchPoints = (navigator.maxTouchPoints ?? 0) > 1;

   return /iPad/i.test(userAgent)
      || (hasMultipleTouchPoints && (platform === 'MacIntel' || /Macintosh/i.test(userAgent)));
};

export const getMobileKeyboardPreference = () => {
   try {
      const savedPreference = localStorage.getItem(KEYBOARD_PREFERENCE_KEY);
      return savedPreference === null ? isIPadOSDevice() : savedPreference === 'true';
   } catch {
      return isIPadOSDevice();
   }
};

export const setMobileKeyboardPreference = (enabled) => {
   try {
      localStorage.setItem(KEYBOARD_PREFERENCE_KEY, String(enabled));
   } catch {
      // The preference remains available for this page when storage is blocked.
   }

   window.dispatchEvent(new CustomEvent(KEYBOARD_PREFERENCE_EVENT, { detail: enabled }));
};

export default function MobileVirtualKeyboard({
   value = '',
   onChange,
   multiline = false,
   disabled = false,
   placeholder = ''
}) {
   const [isOpen, setIsOpen] = useState(false);
   const [useVirtualKeyboard, setUseVirtualKeyboard] = useState(getMobileKeyboardPreference);
   const [layout, setLayout] = useState('letters');
   const [shiftMode, setShiftMode] = useState('off');
   const [cursor, setCursor] = useState(String(value ?? '').length);
   const [selectionRange, setSelectionRange] = useState(null);
   const [isSpaceCursorDragging, setIsSpaceCursorDragging] = useState(false);
   const fieldRef = useRef(null);
   const previewRef = useRef(null);
   const caretRef = useRef(null);
   const lastShiftPressRef = useRef(0);
   const keyRepeatDelayRef = useRef(null);
   const keyRepeatIntervalRef = useRef(null);
   const handleKeyRef = useRef(null);
   const previewHoldTimerRef = useRef(null);
   const previewPointerRef = useRef(null);
   const lastPreviewTapRef = useRef(null);
   const selectionHandleRef = useRef(null);
   const spaceCursorDragRef = useRef(null);
   const normalizedValue = String(value ?? '');
   const hasSelection = selectionRange !== null && selectionRange.end > selectionRange.start;

   useEffect(() => {
      setCursor((current) => Math.min(current, normalizedValue.length));
      setSelectionRange((current) => {
         if (!current) return null;
         const start = Math.min(current.start, normalizedValue.length);
         const end = Math.min(current.end, normalizedValue.length);
         return end > start ? { start, end } : null;
      });
   }, [normalizedValue.length]);

   useEffect(() => {
      if (!isOpen) return undefined;

      const handleKeyDown = (event) => {
         if (event.key === 'Escape') setIsOpen(false);
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
         document.removeEventListener('keydown', handleKeyDown);
         clearTimeout(keyRepeatDelayRef.current);
         clearInterval(keyRepeatIntervalRef.current);
      };
   }, [isOpen]);

   useEffect(() => () => {
      clearTimeout(keyRepeatDelayRef.current);
      clearInterval(keyRepeatIntervalRef.current);
      clearTimeout(previewHoldTimerRef.current);
   }, []);

   useEffect(() => {
      const syncKeyboardPreference = (event) => {
         setUseVirtualKeyboard(Boolean(event.detail));
         setIsOpen(false);
      };

      window.addEventListener(KEYBOARD_PREFERENCE_EVENT, syncKeyboardPreference);
      return () => window.removeEventListener(KEYBOARD_PREFERENCE_EVENT, syncKeyboardPreference);
   }, []);

   useEffect(() => {
      if (!isOpen || !previewRef.current || !caretRef.current) return;

      const preview = previewRef.current;
      const caretTop = caretRef.current.offsetTop;
      const caretBottom = caretTop + caretRef.current.offsetHeight;

      if (caretBottom > preview.scrollTop + preview.clientHeight) {
         preview.scrollTop = caretBottom - preview.clientHeight + 8;
      } else if (caretTop < preview.scrollTop) {
         preview.scrollTop = Math.max(0, caretTop - 8);
      }
   }, [cursor, isOpen, normalizedValue]);

   const openKeyboard = (event) => {
      if (disabled || !useVirtualKeyboard) return;
      event?.preventDefault();
      setCursor(normalizedValue.length);
      setSelectionRange(null);
      setLayout('letters');
      setShiftMode('off');
      lastShiftPressRef.current = 0;
      setIsOpen(true);
      fieldRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
   };

   const replaceAtCursor = (text, removeBefore = 0) => {
      const start = hasSelection ? selectionRange.start : Math.max(0, cursor - removeBefore);
      const end = hasSelection ? selectionRange.end : cursor;
      const nextValue = normalizedValue.slice(0, start) + text + normalizedValue.slice(end);
      setCursor(start + text.length);
      setSelectionRange(null);
      onChange?.(nextValue);
   };

   const getTextPositionFromPoint = (clientX, clientY) => {
      const preview = previewRef.current;
      if (!preview) return cursor;

      let positionNode = null;
      let positionOffset = 0;

      if (document.caretPositionFromPoint) {
         const position = document.caretPositionFromPoint(clientX, clientY);
         positionNode = position?.offsetNode ?? null;
         positionOffset = position?.offset ?? 0;
      } else if (document.caretRangeFromPoint) {
         const rangeAtPoint = document.caretRangeFromPoint(clientX, clientY);
         positionNode = rangeAtPoint?.startContainer ?? null;
         positionOffset = rangeAtPoint?.startOffset ?? 0;
      }

      if (!positionNode || !preview.contains(positionNode)) {
         const bounds = preview.getBoundingClientRect();
         return clientX < bounds.left + (bounds.width / 2) ? 0 : normalizedValue.length;
      }

      const textRange = document.createRange();
      textRange.selectNodeContents(preview);

      try {
         textRange.setEnd(positionNode, positionOffset);
         return Math.min(normalizedValue.length, textRange.toString().length);
      } catch {
         return normalizedValue.length;
      }
   };

   const selectWordAt = (position) => {
      if (!normalizedValue.length) return;

      const characterIndex = Math.min(position, normalizedValue.length - 1);
      const isWordCharacter = (character) => /[\p{L}\p{N}_]/u.test(character ?? '');
      let start = characterIndex;
      let end = characterIndex + 1;

      if (isWordCharacter(normalizedValue[characterIndex])) {
         while (start > 0 && isWordCharacter(normalizedValue[start - 1])) start -= 1;
         while (end < normalizedValue.length && isWordCharacter(normalizedValue[end])) end += 1;
      }

      setSelectionRange({ start, end });
      setCursor(end);
   };

   const handlePreviewPointerDown = (event) => {
      event.preventDefault();
      const position = getTextPositionFromPoint(event.clientX, event.clientY);
      const now = Date.now();
      const lastTap = lastPreviewTapRef.current;
      const isDoubleTap = lastTap
         && now - lastTap.time <= DOUBLE_TAP_DELAY_MS
         && Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) <= DOUBLE_TAP_DISTANCE_PX;

      event.currentTarget.setPointerCapture?.(event.pointerId);
      previewPointerRef.current = {
         pointerId: event.pointerId,
         startX: event.clientX,
         startY: event.clientY,
         position,
         dragging: false,
         doubleTap: Boolean(isDoubleTap),
         longPressed: false
      };

      if (isDoubleTap) {
         lastPreviewTapRef.current = null;
         clearTimeout(previewHoldTimerRef.current);
         selectWordAt(position);
         return;
      }

      setCursor(position);
      setSelectionRange(null);
      clearTimeout(previewHoldTimerRef.current);
      previewHoldTimerRef.current = setTimeout(() => {
         const pointer = previewPointerRef.current;
         if (pointer && !pointer.dragging) {
            pointer.longPressed = true;
            selectWordAt(position);
         }
      }, TEXT_SELECTION_HOLD_MS);
   };

   const handlePreviewPointerMove = (event) => {
      const pointer = previewPointerRef.current;
      if (!pointer || pointer.pointerId !== event.pointerId) return;

      event.preventDefault();
      const distance = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY);
      if (distance >= POINTER_DRAG_THRESHOLD_PX) {
         pointer.dragging = true;
         clearTimeout(previewHoldTimerRef.current);
      }

      if (pointer.dragging) {
         setSelectionRange(null);
         setCursor(getTextPositionFromPoint(event.clientX, event.clientY));
      }
   };

   const stopPreviewPointer = (event) => {
      const pointer = previewPointerRef.current;
      clearTimeout(previewHoldTimerRef.current);

      if (pointer && !pointer.dragging && !pointer.doubleTap && !pointer.longPressed) {
         lastPreviewTapRef.current = {
            time: Date.now(),
            x: event.clientX,
            y: event.clientY
         };
      }

      previewPointerRef.current = null;
   };

   const cancelPreviewPointer = () => {
      clearTimeout(previewHoldTimerRef.current);
      previewPointerRef.current = null;
   };

   const startSelectionHandleDrag = (event, edge) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      selectionHandleRef.current = { pointerId: event.pointerId, edge };
   };

   const moveSelectionHandle = (event) => {
      const activeHandle = selectionHandleRef.current;
      if (!activeHandle || activeHandle.pointerId !== event.pointerId) return;

      event.preventDefault();
      const position = getTextPositionFromPoint(event.clientX, event.clientY);
      setSelectionRange((current) => {
         if (!current) return null;

         if (activeHandle.edge === 'start') {
            const start = Math.min(position, current.end - 1);
            setCursor(current.end);
            return { start, end: current.end };
         }

         const end = Math.max(position, current.start + 1);
         setCursor(end);
         return { start: current.start, end };
      });
   };

   const stopSelectionHandleDrag = (event) => {
      if (selectionHandleRef.current?.pointerId === event.pointerId) {
         selectionHandleRef.current = null;
      }
   };

   const handleKey = (key) => {
      if (key === 'shift') {
         const now = Date.now();

         if (shiftMode === 'locked') {
            setShiftMode('off');
            lastShiftPressRef.current = 0;
         } else if (now - lastShiftPressRef.current <= SHIFT_LOCK_DELAY_MS) {
            setShiftMode('locked');
            lastShiftPressRef.current = 0;
         } else if (shiftMode === 'once') {
            setShiftMode('off');
            lastShiftPressRef.current = 0;
         } else {
            setShiftMode('once');
            lastShiftPressRef.current = now;
         }
         return;
      }
      if (key === 'symbols') {
         setLayout('symbols');
         return;
      }
      if (key === 'symbols2') {
         setLayout('more-symbols');
         return;
      }
      if (key === 'letters') {
         setLayout('letters');
         return;
      }
      if (key === 'backspace') {
         if (hasSelection || cursor > 0) replaceAtCursor('', 1);
         return;
      }
      if (key === 'left') {
         if (hasSelection) {
            setCursor(selectionRange.start);
            setSelectionRange(null);
         } else {
            setCursor((current) => Math.max(0, current - 1));
         }
         return;
      }
      if (key === 'right') {
         if (hasSelection) {
            setCursor(selectionRange.end);
            setSelectionRange(null);
         } else {
            setCursor((current) => Math.min(normalizedValue.length, current + 1));
         }
         return;
      }
      if (key === 'enter') {
         if (multiline) replaceAtCursor('\n');
         else setIsOpen(false);
         return;
      }

      const character = key === 'space' ? ' ' : (shiftMode !== 'off' ? key.toUpperCase() : key);
      replaceAtCursor(character);
      if (shiftMode === 'once') {
         setShiftMode('off');
         lastShiftPressRef.current = 0;
      }
   };

   handleKeyRef.current = handleKey;

   const isRepeatableKey = (key) => key.length === 1
      || ['space', 'backspace', 'left', 'right'].includes(key);

   const stopKeyRepeat = () => {
      clearTimeout(keyRepeatDelayRef.current);
      clearInterval(keyRepeatIntervalRef.current);
      keyRepeatDelayRef.current = null;
      keyRepeatIntervalRef.current = null;
   };

   const startKeyRepeat = (event, key) => {
      if (!isRepeatableKey(key) || (event.button !== undefined && event.button !== 0)) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      handleKeyRef.current?.(key);

      clearTimeout(keyRepeatDelayRef.current);
      clearInterval(keyRepeatIntervalRef.current);
      keyRepeatDelayRef.current = setTimeout(() => {
         keyRepeatIntervalRef.current = setInterval(() => {
            handleKeyRef.current?.(key);
         }, KEY_REPEAT_INTERVAL_MS);
      }, KEY_REPEAT_DELAY_MS);
   };

   const startSpaceCursorDrag = (event) => {
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      stopKeyRepeat();
      spaceCursorDragRef.current = {
         pointerId: event.pointerId,
         startX: event.clientX,
         startCursor: hasSelection ? selectionRange.end : cursor,
         dragging: false
      };
   };

   const moveSpaceCursorDrag = (event) => {
      const drag = spaceCursorDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      event.preventDefault();
      const distanceX = event.clientX - drag.startX;
      if (!drag.dragging && Math.abs(distanceX) < SPACE_CURSOR_DRAG_THRESHOLD_PX) return;

      if (!drag.dragging) {
         drag.dragging = true;
         setSelectionRange(null);
         setIsSpaceCursorDragging(true);
      }

      const characterOffset = Math.trunc(distanceX / SPACE_CURSOR_STEP_PX);
      setCursor(Math.max(0, Math.min(normalizedValue.length, drag.startCursor + characterOffset)));
   };

   const stopSpaceCursorDrag = (event) => {
      const drag = spaceCursorDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      if (!drag.dragging) handleKeyRef.current?.('space');
      spaceCursorDragRef.current = null;
      setIsSpaceCursorDragging(false);
   };

   const cancelSpaceCursorDrag = () => {
      spaceCursorDragRef.current = null;
      setIsSpaceCursorDragging(false);
   };

   const rows = layout === 'letters'
      ? LETTER_ROWS
      : (layout === 'symbols' ? SYMBOL_ROWS : MORE_SYMBOL_ROWS);

   const keyboard = isOpen && ReactDOM.createPortal(
      <div
         className="mobile-virtual-keyboard-backdrop"
         role="presentation"
      >
         <section
            className="mobile-virtual-keyboard"
            role="dialog"
            aria-modal="true"
            aria-label="On-screen keyboard"
         >
            <div className="mobile-virtual-keyboard-header">
               <span>Type your answer</span>
               <button type="button" onClick={() => setIsOpen(false)}>Done</button>
            </div>
            <div
               ref={previewRef}
               className={`mobile-virtual-keyboard-preview ${multiline ? 'is-multiline' : ''} ${isSpaceCursorDragging ? 'is-cursor-dragging' : ''}`}
               role="textbox"
               aria-label="Answer preview"
               aria-readonly="true"
               onContextMenu={(event) => event.preventDefault()}
               onPointerDown={handlePreviewPointerDown}
               onPointerMove={handlePreviewPointerMove}
               onPointerUp={stopPreviewPointer}
               onPointerCancel={cancelPreviewPointer}
               onLostPointerCapture={cancelPreviewPointer}
            >
               {hasSelection ? (
                  <>
                     <span>{normalizedValue.slice(0, selectionRange.start)}</span>
                     <span
                        className="mobile-virtual-keyboard-selection-handle is-start"
                        aria-hidden="true"
                        onPointerDown={(event) => startSelectionHandleDrag(event, 'start')}
                        onPointerMove={moveSelectionHandle}
                        onPointerUp={stopSelectionHandleDrag}
                        onPointerCancel={stopSelectionHandleDrag}
                     />
                     <span className="mobile-virtual-keyboard-selection">
                        {normalizedValue.slice(selectionRange.start, selectionRange.end)}
                     </span>
                     <span
                        ref={caretRef}
                        className="mobile-virtual-keyboard-selection-handle is-end"
                        aria-hidden="true"
                        onPointerDown={(event) => startSelectionHandleDrag(event, 'end')}
                        onPointerMove={moveSelectionHandle}
                        onPointerUp={stopSelectionHandleDrag}
                        onPointerCancel={stopSelectionHandleDrag}
                     />
                     <span>{normalizedValue.slice(selectionRange.end)}</span>
                  </>
               ) : (
                  <>
                     <span>{normalizedValue.slice(0, cursor)}</span>
                     <span ref={caretRef} className="mobile-virtual-keyboard-caret" aria-hidden="true" />
                     <span>{normalizedValue.slice(cursor)}</span>
                  </>
               )}
            </div>
            <div className="mobile-virtual-keyboard-cursor-status" aria-live="polite">
               {isSpaceCursorDragging && `Cursor: ${cursor + 1} of ${normalizedValue.length + 1}`}
            </div>
            <div className="mobile-virtual-keyboard-keys">
               {rows.map((row, rowIndex) => (
                  <div className="mobile-virtual-keyboard-row" key={rowIndex}>
                     {row.map((key) => {
                        const isWide = ['shift', 'backspace', 'symbols', 'symbols2', 'letters', 'enter'].includes(key);
                        const isSpace = key === 'space';
                        const isLetter = /^[a-z]$/.test(key);
                        const usesKeyboardIcon = ['shift', 'backspace', 'enter'].includes(key);
                        const label = isSpace && isSpaceCursorDragging
                           ? '←  Move cursor  →'
                           : usesKeyboardIcon
                           ? <KeyboardKeyIcon type={key} locked={key === 'shift' && shiftMode === 'locked'} />
                           : (isLetter && shiftMode !== 'off'
                              ? key.toUpperCase()
                              : (KEY_LABELS[key] ?? key));
                        return (
                           <button
                              type="button"
                              key={key}
                              className={`${isWide ? 'is-wide' : ''} ${isSpace ? 'is-space' : ''} ${isSpace && isSpaceCursorDragging ? 'is-cursor-dragging' : ''} ${key === 'shift' && shiftMode !== 'off' ? 'is-active' : ''} ${key === 'shift' && shiftMode === 'locked' ? 'is-locked' : ''}`}
                              aria-label={key === 'shift' && shiftMode === 'locked' ? 'Caps lock' : key}
                              aria-pressed={key === 'shift' ? shiftMode !== 'off' : undefined}
                              onPointerDown={(event) => (isSpace
                                 ? startSpaceCursorDrag(event)
                                 : startKeyRepeat(event, key))}
                              onPointerMove={isSpace ? moveSpaceCursorDrag : undefined}
                              onPointerUp={isSpace ? stopSpaceCursorDrag : stopKeyRepeat}
                              onPointerCancel={isSpace ? cancelSpaceCursorDrag : stopKeyRepeat}
                              onLostPointerCapture={isSpace ? cancelSpaceCursorDrag : stopKeyRepeat}
                              onClick={(event) => {
                                 // Repeatable pointer keys are handled on pointerdown.
                                 // detail === 0 identifies keyboard-generated clicks.
                                 if (!isRepeatableKey(key) || event.detail === 0) handleKey(key);
                              }}
                           >
                              {label}
                           </button>
                        );
                     })}
                  </div>
               ))}
            </div>
         </section>
      </div>,
      document.body
   );

   const virtualFieldProps = {
      ref: fieldRef,
      value: normalizedValue,
      readOnly: true,
      inputMode: 'none',
      autoComplete: 'off',
      disabled,
      placeholder,
      className: 'mobile-virtual-keyboard-field',
      onMouseUp: openKeyboard,
      onClick: (event) => {
         // Preserve activation from the keyboard without reopening after a mouse release.
         if (event.detail === 0) openKeyboard(event);
      },
      'aria-haspopup': 'dialog',
      'aria-expanded': isOpen
   };

   const nativeFieldProps = {
      ref: fieldRef,
      value: normalizedValue,
      disabled,
      autoComplete: 'off',
      className: 'mobile-virtual-keyboard-field',
      onChange: (event) => onChange?.(event.target.value)
   };

   return (
      <>
         {useVirtualKeyboard
            ? (multiline ? <Input.TextArea {...virtualFieldProps} rows={8} /> : <Input {...virtualFieldProps} />)
            : (multiline ? <Input.TextArea {...nativeFieldProps} rows={8} /> : <Input {...nativeFieldProps} />)}
         {keyboard}
      </>
   );
}
